<div class="page-container">
  <div class="back-button-wrapper">
    <p-button
      icon="pi pi-arrow-left"
      label="Back to Healthy Gourmet Kitchens"
      routerLink="/producers"
      severity="secondary"
    ></p-button>
  </div>

  @if (producer(); as producer) {
    <div class="header-with-status">
      <h1 class="producer-title">
        {{ producer.name }}
        <p-tag class="status-chip" [severity]="statusSeverity()" [value]="producer.status"></p-tag>
        <p-button
          icon="pi pi-pencil"
          label="Change status"
          severity="secondary"
          size="small"
          [text]="true"
          (onClick)="openStatusDialog()"
        ></p-button>
      </h1>
    </div>

    @if (statusUpdateError()) {
      <p-message
        severity="error"
        text="Updating the Healthy Gourmet Kitchen status failed. Please try again."
      ></p-message>
    }

    <app-change-status-dialog
      [currentStatus]="producer.status"
      [producerName]="producer.name"
      [submitting]="statusSubmitting()"
      [(visible)]="statusDialogVisible"
      (confirm)="changeStatus($event)"
    ></app-change-status-dialog>

    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
      <p-tablist>
        <p-tab [value]="DETAILS_TAB">Details</p-tab>
        <p-tab [value]="MENU_ITEMS_TAB">Menu Items</p-tab>
        <p-tab [value]="ONBOARDING_TAB">Onboarding</p-tab>
      </p-tablist>

      <p-tabpanels>
        <p-tabpanel [value]="DETAILS_TAB">
          <div class="producer-content">
            <p-card header="Contact" styleClass="detail-card">
              <div class="detail-row">
                <strong>Email:</strong>
                <span>{{ producer.email || 'Not provided' }}</span>
              </div>
              <div class="detail-row">
                <strong>Phone:</strong>
                <span>{{ producer.phone || 'Not provided' }}</span>
              </div>
              @if (producer.secondaryEmail) {
                <div class="detail-row">
                  <strong>Secondary email:</strong>
                  <span>{{ producer.secondaryEmail }}</span>
                </div>
              }
              @if (producer.secondaryPhone) {
                <div class="detail-row">
                  <strong>Secondary phone:</strong>
                  <span>{{ producer.secondaryPhone }}</span>
                </div>
              }
              @if (formatAddress(producer.address); as address) {
                <div class="detail-row">
                  <strong>Address:</strong>
                  <span>{{ address }}</span>
                </div>
              }
              @if (producer.url) {
                <div class="detail-row">
                  <strong>Website:</strong>
                  <a target="_blank" rel="noopener" [href]="producer.url">{{ producer.url }}</a>
                </div>
              }
              @if (producer.businessTypes?.length) {
                <div class="detail-row">
                  <strong>Business types:</strong>
                  <span>{{ producer.businessTypes?.join(', ') }}</span>
                </div>
              }
              @if (producer.marketingBlurb) {
                <div class="detail-row">
                  <strong>Marketing blurb:</strong>
                  <span>{{ producer.marketingBlurb }}</span>
                </div>
              }
              <div class="detail-row">
                <strong>Created:</strong>
                <span>{{ producer.createdOn | date: 'medium' }}</span>
              </div>
              @if (producer.modifiedOn) {
                <div class="detail-row">
                  <strong>Last updated:</strong>
                  <span>{{ producer.modifiedOn | date: 'medium' }}</span>
                </div>
              }
            </p-card>

            <p-card header="Establishments ({{ producer.establishments.length }})" styleClass="detail-card">
              <ul class="establishment-list">
                @for (establishment of producer.establishments; track establishment.id) {
                  <li class="establishment-item">
                    <strong class="establishment-name">{{ establishment.name }}</strong>
                    @if (establishment.email) {
                      <div class="detail-row">
                        <strong>Email:</strong>
                        <span>{{ establishment.email }}</span>
                      </div>
                    }
                    @if (establishment.phone) {
                      <div class="detail-row">
                        <strong>Phone:</strong>
                        <span>{{ establishment.phone }}</span>
                      </div>
                    }
                    @if (establishment.secondaryEmail) {
                      <div class="detail-row">
                        <strong>Secondary email:</strong>
                        <span>{{ establishment.secondaryEmail }}</span>
                      </div>
                    }
                    @if (establishment.secondaryPhone) {
                      <div class="detail-row">
                        <strong>Secondary phone:</strong>
                        <span>{{ establishment.secondaryPhone }}</span>
                      </div>
                    }
                    @if (formatAddress(establishment.address); as address) {
                      <div class="detail-row">
                        <strong>Address:</strong>
                        <span>{{ address }}</span>
                      </div>
                    }
                    @if (establishment.taxId) {
                      <div class="detail-row">
                        <strong>Tax ID:</strong>
                        <span>{{ establishment.taxId }}</span>
                      </div>
                    }
                    @if (establishment.fulfillmentTypes?.length) {
                      <div class="detail-row">
                        <strong>Fulfillment:</strong>
                        <span>{{ establishment.fulfillmentTypes?.join(', ') }}</span>
                      </div>
                    }
                  </li>
                } @empty {
                  <p class="empty-state">No establishments registered.</p>
                }
              </ul>
            </p-card>
          </div>
        </p-tabpanel>

        <p-tabpanel [value]="MENU_ITEMS_TAB">
          <div class="menu-items-tab">
            <div class="menu-items-toolbar">
              <p-select
                class="w-14rem"
                placeholder="All statuses"
                [options]="statusOptions"
                [showClear]="true"
                [(ngModel)]="selectedStatus"
                (onChange)="onStatusFilterChange()"
              ></p-select>

              @if (establishments().length > 1) {
                <p-select
                  class="w-14rem"
                  optionLabel="name"
                  optionValue="id"
                  [options]="establishments()"
                  [ngModel]="selectedEstablishmentId()"
                  (ngModelChange)="selectedEstablishmentId.set($event)"
                ></p-select>
              }
            </div>

            @if (menuItemsError()) {
              <p-message severity="error" text="Failed to load menu items. Please try again."></p-message>
            }

            <p-table
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              dataKey="id"
              [lazy]="true"
              [loading]="menuItemsLoading()"
              [paginator]="true"
              [rows]="menuItemsPageSize()"
              [rowHover]="true"
              [showCurrentPageReport]="true"
              [totalRecords]="menuItemsTotalRecords()"
              [value]="menuItemRows()"
              (onPage)="onMenuItemsPage($event)"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th class="thumb-col"></th>
                  <th>Name</th>
                  <th>Status</th>
                  <th class="stock-col">{{ stockColumnHeader }}</th>
                </tr>
              </ng-template>

              <ng-template let-item pTemplate="body">
                <tr class="menu-item-row" (click)="openMenuItem(item)">
                  <td class="thumb-col">
                    @if (item.imageUrl) {
                      <img class="thumb" appImageFallback [alt]="item.name" [src]="item.imageUrl" />
                    } @else {
                      <div class="thumb thumb--placeholder">
                        <i class="pi pi-image"></i>
                      </div>
                    }
                  </td>
                  <td>
                    <div class="name-cell">
                      <span class="name">{{ item.name }}</span>
                      @if (item.marketingName) {
                        <span class="marketing-name">{{ item.marketingName }}</span>
                      }
                    </div>
                  </td>
                  <td>
                    @if (item.status) {
                      <p-tag [severity]="getMenuItemStatusSeverity(item.status)" [value]="item.status"></p-tag>
                    }
                  </td>
                  <td class="stock-col">
                    <app-inventory-stock-cell [days]="stockCellsFor(item)"></app-inventory-stock-cell>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td class="empty-message-cell" colspan="4">
                    <p-message severity="info" text="No menu items yet for this Healthy Gourmet Kitchen."></p-message>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="loadingbody">
                @for (i of [1, 2, 3, 4, 5]; track i) {
                  <tr>
                    <td><p-skeleton height="3rem" width="3rem"></p-skeleton></td>
                    <td><p-skeleton></p-skeleton></td>
                    <td><p-skeleton></p-skeleton></td>
                    <td><p-skeleton></p-skeleton></td>
                  </tr>
                }
              </ng-template>
            </p-table>
          </div>
        </p-tabpanel>

        <p-tabpanel [value]="ONBOARDING_TAB">
          <app-producer-onboarding-review
            [onboarding]="onboarding()"
            [loading]="onboardingLoading()"
            [error]="onboardingError()"
          ></app-producer-onboarding-review>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  } @else if (loading()) {
    <div class="loading-state">
      <p-skeleton width="20rem" height="2rem"></p-skeleton>
      <p-skeleton width="100%" height="6rem" styleClass="mt-3"></p-skeleton>
      <p-skeleton width="100%" height="6rem" styleClass="mt-3"></p-skeleton>
    </div>
  } @else if (error()) {
    <p-message severity="error" text="Failed to load Healthy Gourmet Kitchen details. Please try again."></p-message>
  } @else {
    <app-page-header title="Healthy Gourmet Kitchen Details"></app-page-header>
  }
</div>
